import { useLinkedInInfo } from 'components/molecules/linkedInNotLogged/useLinkedInInfo'
import {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useMemo
} from 'react'

import type { LinkedAccountHint } from './LinkedInLoginModal'
import { LinkedInLoginModal } from './LinkedInLoginModal'
import { useLinkedInPasswordLogin } from './useLinkedInPasswordLogin'

type LinkedInReauthContextValue = {
	start: () => void
}

const LinkedInReauthContext = createContext<LinkedInReauthContextValue | null>(
	null
)

function buildLinkedAccountHint(
	linkedInfo: {
		familyName?: string
		givenName?: string
		linkedInProfileUrl?: string
		publicIdentifier?: string
		publicProfileUrl?: string
	} | null | undefined,
	id: string | null | undefined
): LinkedAccountHint | null {
	if (!linkedInfo) {
		const result = null
		return result
	}

	const displayName = `${linkedInfo.givenName ?? ''} ${linkedInfo.familyName ?? ''}`.trim()
	if (!displayName) {
		const result = null
		return result
	}

	const href =
		linkedInfo.publicProfileUrl ||
		linkedInfo.linkedInProfileUrl ||
		(id ? `https://www.linkedin.com/in/${id}/` : '') ||
		(linkedInfo.publicIdentifier
			? `https://www.linkedin.com/in/${linkedInfo.publicIdentifier}/`
			: '')

	if (!href) {
		const result = null
		return result
	}

	const result: LinkedAccountHint = {
		displayName,
		href
	}
	return result
}

function LinkedInReauthProvider({ children }: PropsWithChildren) {
	const login = useLinkedInPasswordLogin()
	const { id, linkedInfo } = useLinkedInInfo()
	const linkedAccount = useMemo(
		() => buildLinkedAccountHint(linkedInfo, id),
		[id, linkedInfo]
	)

	const start = useCallback(() => {
		login.start()
	}, [login.start])

	const value = useMemo(() => ({ start }), [start])

	const result = (
		<LinkedInReauthContext.Provider value={value}>
			{children}
			<LinkedInLoginModal
				description='Sign in with your linked LinkedIn account email and password.'
				linkedAccount={linkedAccount}
				login={login}
				title='Sign in to LinkedIn'
			/>
		</LinkedInReauthContext.Provider>
	)
	return result
}

function useLinkedInReauth() {
	const context = useContext(LinkedInReauthContext)
	if (!context) {
		throw new Error('useLinkedInReauth must be used within LinkedInReauthProvider')
	}
	const result = context
	return result
}

export { LinkedInReauthProvider, useLinkedInReauth }
